import { I18n } from '@/i18n/I18n';
import type { ReportBundleFiles } from './types';

/**
 * What a fixture holds the report to beyond its verdict: what the document
 * prints — a table's cell, a labelled value, a section, some text — and how
 * each of the report's findings comes out. A fixture's own checks are in its
 * `expect.checks`; a rules file in `tests/` holds checks for every fixture (or
 * every fixture that must come to one verdict).
 *
 * Checks on findings are made wherever the definition runs, the server's
 * compile included, so they hold a bundle back from publishing like its
 * verdict. Checks on the printed document need it drawn, so they are made
 * where it is: the studio, and the SDK kit under jsdom. Both read the document
 * the way its reader does, by what it says — a table by its caption, a row by
 * its first cell, a column by its heading, a value by its label — never by
 * markup that would change with the next release of a block.
 */

export const RULES_DIR = 'tests/';
export const DEFAULT_RULES_FILE = 'tests/rules.json';
export const isRulesPath = (path: string) => path.startsWith(RULES_DIR) && path.endsWith('.json');

/** Where in the printed document, as a reader would point at it. `*` for a row or a column is every one. */
export type PrintedTarget =
  | { table: string | number; row: string; column: string }
  | { field: string }
  | { section: string }
  | { text: string };

export interface PrintedCheck {
  printed: PrintedTarget;
  /** prints exactly this, spaces collapsed */
  reads?: string;
  contains?: string;
  /** the first number it prints is at least the first and at most the second; null for no bound */
  between?: [number | null, number | null];
  /** false: printed nowhere. Implied true by every other assertion */
  present?: boolean;
  note?: string;
}

export interface FindingCheck {
  /** the finding's id, as the definition makes it */
  finding: string;
  passes: boolean;
  note?: string;
}

export type Check = PrintedCheck | FindingCheck;

export const isPrintedCheck = (check: Check): check is PrintedCheck => 'printed' in check;

/** A check as made: what it found, and why it missed. `check` is null for a rules file that could not be read. */
export interface CheckResult {
  check: Check | null;
  /** the file it is written in: the fixture itself, or a rules file */
  from: string;
  ok: boolean;
  /** what the document printed there, or how the finding came out */
  found?: string[];
  /** why it missed */
  message?: string;
}

/** A rules file: checks for every fixture, or every fixture expected to come to `when.verdict`. */
export interface RulesFile {
  description?: string;
  when?: { verdict?: string };
  checks: Check[];
}

export interface PlannedCheck {
  check: Check;
  from: string;
}

const isObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const isBound = (value: unknown) => value === null || (typeof value === 'number' && Number.isFinite(value));

const PRINTED_KEYS = ['printed', 'reads', 'contains', 'between', 'present', 'note'];
const FINDING_KEYS = ['finding', 'passes', 'note'];

/** One check as written, or why it is not one. */
export function readCheck(raw: unknown): { check: Check | null; problem: string | null } {
  if (!isObject(raw)) return { check: null, problem: 'a check is an object: { "printed": {…}, "reads"?: … } or { "finding": "<id>", "passes": true }' };
  const note = typeof raw.note === 'string' ? { note: raw.note } : {};
  if (!('finding' in raw) && !isObject(raw.printed)) return { check: null, problem: 'a check names what it is about: "printed" (a place in the document) or "finding" (an id)' };
  // a key misspelt ("read" for "reads") would leave a weaker check that holds whatever is printed
  const known = 'finding' in raw ? FINDING_KEYS : PRINTED_KEYS;
  const unknown = Object.keys(raw).filter((k) => !known.includes(k));
  if (unknown.length) return { check: null, problem: `${unknown.map((k) => `"${k}"`).join(', ')}: a check takes ${known.map((k) => `"${k}"`).join(', ')}` };
  if ('finding' in raw) {
    if (typeof raw.finding !== 'string' || raw.finding === '') return { check: null, problem: '"finding" is the id of one of the report\'s findings' };
    if (typeof raw.passes !== 'boolean') return { check: null, problem: `finding "${raw.finding}": "passes" is true or false` };
    return { check: { finding: raw.finding, passes: raw.passes, ...note }, problem: null };
  }
  if (!isObject(raw.printed)) return { check: null, problem: 'a check names what it is about: "printed" (a place in the document) or "finding" (an id)' };
  const at = raw.printed;
  // what it must print goes beside "printed", never in it: { "printed": { "field": "X", "reads": "Y" } } would only ask that X is printed
  const kind = 'table' in at ? ['table', 'row', 'column'] : 'field' in at ? ['field'] : 'section' in at ? ['section'] : 'text' in at ? ['text'] : null;
  const stray = kind ? Object.keys(at).filter((k) => !kind.includes(k)) : [];
  if (stray.length) return { check: null, problem: `${stray.map((k) => `"${k}"`).join(', ')} in "printed": it names a place (${kind!.map((k) => `"${k}"`).join(', ')}); what it must be goes beside it` };
  let printed: PrintedTarget;
  if ('table' in at) {
    if (!(typeof at.table === 'string' || (typeof at.table === 'number' && Number.isInteger(at.table))) || typeof at.row !== 'string' || typeof at.column !== 'string') {
      return { check: null, problem: 'a table cell is { "table": "<caption>" or <number>, "row": "<first cell>", "column": "<heading>" }' };
    }
    printed = { table: at.table, row: at.row, column: at.column };
  } else if (typeof at.field === 'string') printed = { field: at.field };
  else if (typeof at.section === 'string') printed = { section: at.section };
  else if (typeof at.text === 'string') printed = { text: at.text };
  else return { check: null, problem: '"printed" is { "table", "row", "column" }, { "field" }, { "section" } or { "text" }' };
  const check: PrintedCheck = { printed, ...note };
  if (raw.reads !== undefined) {
    if (typeof raw.reads !== 'string') return { check: null, problem: '"reads" is the text it prints' };
    check.reads = raw.reads;
  }
  if (raw.contains !== undefined) {
    if (typeof raw.contains !== 'string') return { check: null, problem: '"contains" is text it prints' };
    check.contains = raw.contains;
  }
  if (raw.between !== undefined) {
    if (!Array.isArray(raw.between) || raw.between.length !== 2 || !raw.between.every(isBound) || raw.between.every((b) => b === null)) return { check: null, problem: '"between" is [lowest, highest], either (not both) null for no bound' };
    check.between = [raw.between[0] as number | null, raw.between[1] as number | null];
  }
  if (raw.present !== undefined) {
    if (typeof raw.present !== 'boolean') return { check: null, problem: '"present" is true or false' };
    check.present = raw.present;
  }
  // some text is found as itself: it is printed or not, and holds nothing else to compare
  if ('text' in printed && (check.reads !== undefined || check.contains !== undefined || check.between)) return { check: null, problem: 'a check on { "text" } says only whether it is printed ("present")' };
  return { check, problem: null };
}

/** The checks in a list, and what is wrong with any that are not checks. */
export function readChecks(raw: unknown): { checks: Check[]; problems: string[] } {
  if (raw === undefined) return { checks: [], problems: [] };
  if (!Array.isArray(raw)) return { checks: [], problems: ['"checks" is a list'] };
  const checks: Check[] = [];
  const problems: string[] = [];
  raw.forEach((item, i) => {
    const { check, problem } = readCheck(item);
    if (check) checks.push(check);
    else problems.push(`check ${i + 1}: ${problem}`);
  });
  return { checks, problems };
}

/** the verdicts a rules file can hold fixtures to */
const RULE_VERDICTS = ['passed', 'failed', 'incomplete'];

/** A rules file as written, or why it is not one. */
export function readRulesFile(text: string): { rules: RulesFile | null; problems: string[] } {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (error) {
    return { rules: null, problems: [`not JSON: ${error instanceof Error ? error.message : String(error)}`] };
  }
  if (!isObject(raw)) return { rules: null, problems: ['a rules file is { "description"?, "when"?: { "verdict"? }, "checks": […] }'] };
  const { checks, problems } = readChecks(raw.checks);
  // a key misspelt ("check" for "checks") would leave a file that holds every fixture to nothing
  const unknown = Object.keys(raw).filter((k) => !['description', 'when', 'checks'].includes(k));
  if (unknown.length) problems.push(`${unknown.map((k) => `"${k}"`).join(', ')}: a rules file takes "description", "when" and "checks"`);
  const verdict = isObject(raw.when) ? raw.when.verdict : undefined;
  const when = typeof verdict === 'string' && RULE_VERDICTS.includes(verdict) ? { verdict } : undefined;
  // a verdict misspelt would hold no fixture, and one of another type every fixture: neither is what was meant
  if ((raw.when !== undefined && !isObject(raw.when)) || (verdict !== undefined && !when)) problems.push(`"when" is { "verdict": ${RULE_VERDICTS.map((v) => `"${v}"`).join(', ').replace(/, ([^,]*)$/, ' or $1')} }`);
  return { rules: { ...(typeof raw.description === 'string' ? { description: raw.description } : {}), ...(when ? { when } : {}), checks }, problems };
}

/** A fixture's own checks, from its `expect`, and what could not be read of them. */
export function readFixtureChecks(expect: unknown): { checks: Check[]; problems: string[] } {
  if (!isObject(expect)) return { checks: [], problems: [] };
  const { checks, problems } = readChecks(expect.checks);
  // "check" for "checks" would hold the fixture to nothing, and say nothing of it
  return { checks, problems: 'check' in expect ? [...problems, '"expect" lists its checks as "checks", not "check"'] : problems };
}

/** The checks a fixture is held to — its own, then each rules file's that applies — and what could not be read. */
export function checksForFixture(file: string, fixture: unknown, files: ReportBundleFiles): { planned: PlannedCheck[]; problems: CheckResult[] } {
  const planned: PlannedCheck[] = [];
  const problems: CheckResult[] = [];
  const expect = isObject(fixture) && isObject(fixture.expect) ? fixture.expect : null;
  const own = readFixtureChecks(expect);
  planned.push(...own.checks.map((check) => ({ check, from: file })));
  problems.push(...own.problems.map((message) => ({ check: null, from: file, ok: false, message })));
  const verdict = typeof expect?.verdict === 'string' ? expect.verdict : null;
  for (const path of Object.keys(files).filter(isRulesPath).sort()) {
    const { rules, problems: unread } = readRulesFile(files[path]);
    problems.push(...unread.map((message) => ({ check: null, from: path, ok: false, message })));
    if (!rules || (rules.when?.verdict && rules.when.verdict !== verdict)) continue;
    planned.push(...rules.checks.map((check) => ({ check, from: path })));
  }
  return { planned, problems };
}

/** A check on a finding, against the findings the report came to. */
export function checkFinding(check: FindingCheck, findings: ReadonlyArray<{ id: string; passed: boolean }>): Pick<CheckResult, 'ok' | 'found' | 'message'> {
  const finding = findings.find((f) => f.id === check.finding);
  if (!finding) return { ok: false, found: [], message: I18n.get('expectNoFinding', { id: check.finding }) };
  const state = I18n.get(finding.passed ? 'expectPasses' : 'expectFails');
  return finding.passed === check.passes ? { ok: true, found: [state] } : { ok: false, found: [state], message: I18n.get('expectFindingCameTo', { id: check.finding, state }) };
}

// ——— the printed document ———

const squash = (s: string | null | undefined) => (s ?? '').replace(/\s+/g, ' ').trim();
const fold = (s: string) => squash(s).toLocaleLowerCase();
const text = (el: Element | null | undefined) => squash(el?.textContent);
/** a label as printed, without what marks it optional or ends it */
const asLabel = (s: string) => fold(s).replace(/\s*\(optional\)$/, '').replace(/:$/, '');
/** a heading or a caption without the number the sheet gives it */
const unnumbered = (s: string) => squash(s).replace(/^\d+(\.\d+)*\s*·\s*/, '').replace(/^(table|figure)\s+\d+\.\s*/i, '');

interface Column { title: string; group: string | null }

function columnsOf(table: HTMLTableElement): Column[] {
  const rows = Array.from(table.tHead?.rows ?? []);
  if (rows.length === 0) return [];
  if (rows.length === 1) return Array.from(rows[0].cells).map((cell) => ({ title: text(cell), group: null }));
  const leaves = Array.from(rows[rows.length - 1].cells);
  const out: Column[] = [];
  let next = 0;
  for (const cell of Array.from(rows[0].cells)) {
    if (cell.rowSpan > 1) out.push({ title: text(cell), group: null });
    else for (let i = 0; i < Math.max(1, cell.colSpan); i += 1) out.push({ title: text(leaves[next++]), group: text(cell) });
  }
  return out;
}

const columnMatches = (column: Column, wanted: string) => wanted === '*' || [column.title, column.group ? `${column.group} ${column.title}` : null, column.group ? `${column.group} · ${column.title}` : null].some((t) => t !== null && fold(t) === fold(wanted));

function tableMatches(table: HTMLTableElement, wanted: string | number): boolean {
  const caption = text(table.caption);
  if (typeof wanted === 'number') return new RegExp(`^table\\s+${wanted}\\.`, 'i').test(caption);
  return caption !== '' && fold(unnumbered(caption)) === fold(wanted);
}

/** The rows of a table's body, each as its cells; the one that says the table is empty is no row. */
function bodyRows(table: HTMLTableElement): HTMLTableCellElement[][] {
  return Array.from(table.tBodies).flatMap((body) => Array.from(body.rows)).map((row) => Array.from(row.cells)).filter((cells) => !(cells.length === 1 && cells[0].classList.contains('table-empty')));
}

/** a value that is only the document's placeholder for none */
const unset = (value: Element) => value.children.length === 1 && value.firstElementChild!.classList.contains('unset') && text(value) === text(value.firstElementChild);

/** Every value printed under a label: report fields and sign-offs, summary grids, the header's line, tiles. */
function labelledValues(root: ParentNode, withUnset = false): Array<{ label: string; value: string; element: Element }> {
  const out: Array<{ label: string; value: string; element: Element }> = [];
  root.querySelectorAll('.entry-label').forEach((label) => {
    const value = label.parentElement?.querySelector('.entry-value');
    // a field not recorded, a line unsigned: the label is printed, a value is not
    if (value && (withUnset || !unset(value))) out.push({ label: text(label), value: text(value), element: value });
  });
  root.querySelectorAll('dl.kv').forEach((dl) => {
    const dt = dl.querySelector('dt');
    const dd = dl.querySelector('dd');
    if (dt && dd) out.push({ label: text(dt), value: text(dd), element: dd });
  });
  root.querySelectorAll('.hm-label').forEach((label) => {
    const holder = label.parentElement;
    if (holder) out.push({ label: text(label), value: squash(text(holder).slice(text(label).length)), element: holder });
  });
  root.querySelectorAll('.tile-label').forEach((label) => {
    const value = label.parentElement?.querySelector('.tile-value');
    if (value) out.push({ label: text(label), value: text(value), element: value });
  });
  return out;
}

const HEADINGS = 'h1, h2, h3, h4';

/** What the document prints where the target points: one text for each place it finds, none when it is not printed. */
export function locatePrinted(root: ParentNode, target: PrintedTarget): string[] {
  if ('table' in target) {
    const found: string[] = [];
    for (const table of Array.from(root.querySelectorAll('table')).filter((t) => tableMatches(t, target.table))) {
      const columns = columnsOf(table);
      // every column is every column of values: the first names the row, as `row` reads it
      const at = columns.map((c, i) => (columnMatches(c, target.column) && !(target.column === '*' && i === 0) ? i : -1)).filter((i) => i >= 0);
      for (const cells of bodyRows(table)) {
        if (target.row !== '*' && fold(text(cells[0])) !== fold(target.row)) continue;
        for (const i of at) if (cells[i]) found.push(text(cells[i]));
      }
    }
    return found;
  }
  if ('field' in target) return labelledValues(root).filter((v) => asLabel(v.label) === asLabel(target.field)).map((v) => v.value);
  if ('section' in target) return Array.from(root.querySelectorAll(HEADINGS)).map((h) => unnumbered(text(h))).filter((t) => fold(t) === fold(target.section));
  const all = squash('body' in root ? (root as Document).body?.textContent : (root as Node).textContent);
  return fold(all).includes(fold(target.text)) ? [squash(target.text)] : [];
}

/**
 * The first number printed in a value, as a reader takes it: a minus as the
 * limit block prints it ("−12.5"), commas grouping thousands ("1,234,567"),
 * or one decimal comma ("4,7").
 */
export const firstNumber = (s: string): number | null => {
  const plain = s
    .replace(/[\u2212\u2012-\u2014](?=\d)/g, '-')
    .replace(/(?<![\d.,])\d{1,3}(?:,\d{3})+(?!\d|,\d)/g, (grouped) => grouped.replace(/,/g, ''));
  const match = /-?\d+(?:[.,]\d+)?(?:e[-+]?\d+)?/i.exec(plain);
  return match ? Number(match[0].replace(',', '.')) : null;
};

/** A check on the printed document, made against the drawn document. */
export function checkPrinted(root: ParentNode, check: PrintedCheck): Pick<CheckResult, 'ok' | 'found' | 'message'> {
  const found = locatePrinted(root, check.printed);
  if (check.present === false) return found.length === 0 ? { ok: true, found } : { ok: false, found, message: I18n.get('expectPrintedAnyway', { found: found.slice(0, 3).join(' · ') }) };
  if (found.length === 0) return { ok: false, found, message: I18n.get('expectNotPrinted') };
  const misses = found.filter((value) => {
    if (check.reads !== undefined && value !== squash(check.reads)) return true;
    if (check.contains !== undefined && !fold(value).includes(fold(check.contains))) return true;
    if (check.between) {
      const n = firstNumber(value);
      const [low, high] = check.between;
      if (n === null || (low !== null && n < low) || (high !== null && n > high)) return true;
    }
    return false;
  });
  if (misses.length === 0) return { ok: true, found };
  return { ok: false, found, message: I18n.get(found.length > 1 ? 'expectPrintedSome' : 'expectPrinted', { found: misses.slice(0, 3).map((m) => `"${m}"`).join(', '), count: String(misses.length), total: String(found.length) }) };
}

/**
 * The target a reader points at by clicking an element of the drawn
 * document: the cell of a captioned table, a labelled value, a heading, or
 * else the text clicked. Null outside the document's text.
 */
export function targetAt(element: Element, root: ParentNode): PrintedTarget | null {
  const cell = element.closest('td, th');
  const table = cell?.closest('table') as HTMLTableElement | null;
  if (cell && table && table.caption && cell.closest('tbody')) {
    const row = (cell.parentElement as HTMLTableRowElement | null);
    const columns = columnsOf(table);
    const index = row ? Array.from(row.cells).indexOf(cell as HTMLTableCellElement) : -1;
    const caption = text(table.caption);
    const numbered = /^table\s+(\d+)\./i.exec(caption);
    const title = unnumbered(caption);
    if (row && index >= 0 && columns[index]) {
      const column = columns[index];
      // a title another table prints too names both: the number names this one
      const repeated = Array.from(root.querySelectorAll('table')).filter((t) => t.caption && fold(unnumbered(text(t.caption))) === fold(title)).length > 1;
      return { table: numbered && (!title || repeated) ? Number(numbered[1]) : title, row: text(row.cells[0]), column: column.group ? `${column.group} ${column.title}` : column.title };
    }
  }
  const holder = element.closest('.entry, .signoff, dl.kv, .header-meta > span, .tiles > *');
  const labelled = holder ? labelledValues(root, true).find((v) => v.element === holder || holder.contains(v.element)) : undefined;
  if (labelled) return { field: asLabelCase(labelled.label) };
  const heading = element.closest(HEADINGS);
  if (heading) return { section: unnumbered(text(heading)) };
  const said = text(element);
  return said ? { text: said.length > 80 ? said.slice(0, 80) : said } : null;
}

/** a label as written, without its "(optional)" */
const asLabelCase = (s: string) => squash(s).replace(/\s*\(optional\)$/i, '').replace(/:$/, '');

// ——— said in words ———

const quote = (s: string) => `“${s}”`;

export function describeTarget(target: PrintedTarget): string {
  if ('table' in target) {
    const table = typeof target.table === 'number' ? I18n.get('expectTableNumber', { number: String(target.table) }) : quote(target.table);
    const row = target.row === '*' ? I18n.get('expectEveryRow') : target.row;
    const column = target.column === '*' ? I18n.get('expectEveryColumn') : target.column;
    return I18n.get('expectCell', { table, row, column });
  }
  if ('field' in target) return target.field;
  if ('section' in target) return I18n.get('expectSection', { title: target.section });
  return quote(target.text);
}

export function describeAssertion(check: Check): string {
  if (!isPrintedCheck(check)) return I18n.get(check.passes ? 'expectFindingPasses' : 'expectFindingFails', { id: check.finding });
  if (check.present === false) return I18n.get('expectAbsent');
  const parts: string[] = [];
  if (check.reads !== undefined) parts.push(I18n.get('expectReads', { text: check.reads }));
  if (check.contains !== undefined) parts.push(I18n.get('expectContains', { text: check.contains }));
  if (check.between) {
    const [low, high] = check.between;
    parts.push(low !== null && high !== null ? I18n.get('expectBetween', { low: String(low), high: String(high) }) : low !== null ? I18n.get('expectAtLeast', { low: String(low) }) : I18n.get('expectAtMost', { high: String(high) }));
  }
  return parts.length ? parts.join(I18n.get('expectAnd')) : I18n.get('expectPresent');
}

/** A check in a line: what it is about, and what it must be. */
export function describeCheck(check: Check): string {
  return isPrintedCheck(check) ? `${describeTarget(check.printed)} ${describeAssertion(check)}` : describeAssertion(check);
}
